import { useEffect, useMemo, useState } from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { fetchStaffUptime, fetchUptime } from './api.js';
import { formatLocalDate } from './format.js';
import type { UptimeDay } from './types.js';

interface Props {
  service: string;
  days?: number;
  /** Staff view: fetch from /api/staff/uptime and show a numeric percentage. */
  staff?: boolean;
}

interface Square {
  /** ISO date string (YYYY-MM-DD). */
  date: string;
  data: UptimeDay | null;
}

/**
 * Resolve an MUI palette colour for a day's uptime percentage.
 *
 * - >= 99.99% → success.main
 * - >=    99% → success.light
 * - >=    95% → warning.main
 * - <     95% → error.main
 * - no data   → divider (pale grey, picks up theme)
 */
function colourFor(theme: Theme, day: UptimeDay | null): string {
  if (!day || day.total_polls === 0) return theme.palette.divider;
  const pct = day.uptime_pct;
  if (pct >= 0.9999) return theme.palette.success.main;
  if (pct >= 0.99) return theme.palette.success.light;
  if (pct >= 0.95) return theme.palette.warning.main;
  return theme.palette.error.main;
}

function tooltipText(square: Square): string {
  const { date, data } = square;
  const label = formatLocalDate(date);
  if (!data || data.total_polls === 0) return `${label} — no data`;
  const pct = (data.uptime_pct * 100).toFixed(2);
  // synthetic_runner polls roughly every minute, so down_polls ≈ down minutes.
  const downMin = data.down_polls;
  return `${label} — ${pct}% uptime, ${downMin} min down`;
}

/**
 * GitHub-contribution-style 90-day uptime bar. One square per day, oldest on
 * the left, today on the right. Empty / future days render in `theme.palette.divider`.
 */
export function UptimeBar({ service, days = 90, staff = false }: Props) {
  const theme = useTheme<Theme>();
  const [data, setData] = useState<UptimeDay[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = staff ? await fetchStaffUptime(service, days) : await fetchUptime(service, days);
        if (!cancelled) setData(res.days);
      } catch {
        // Silent — the service status row is the canonical signal; an empty
        // bar is fine fallback.
        if (!cancelled) setData([]);
      }
    };
    tick();
    // 5 minutes, not 30s. Uptime is bucketed by UTC day: a closed day's square
    // can never change and today's moves by ~1/1440th per poll. This interval
    // runs once per rendered service, so it is the main uptime-load driver.
    // Service up/down status keeps its own 30s poll in App.tsx.
    const id = setInterval(tick, 300_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [service, days, staff]);

  const squares = useMemo<Square[]>(() => {
    const byDate = new Map<string, UptimeDay>();
    for (const d of data ?? []) byDate.set(d.date, d);

    const out: Square[] = [];
    const today = new Date();
    for (let i = days - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setUTCDate(today.getUTCDate() - i);
      const iso = d.toISOString().slice(0, 10);
      out.push({ date: iso, data: byDate.get(iso) ?? null });
    }
    return out;
  }, [data, days]);

  // SVG sizing: each square 8×16 with 2px gap. Total width ≈ days * 10.
  const squareW = 8;
  const squareH = 16;
  const gap = 2;
  const width = days * (squareW + gap) - gap;

  // Poll-weighted uptime across the window — total polls, not a mean of daily
  // percentages, so days with fewer polls don't skew the figure.
  const overallPct = useMemo<number | null>(() => {
    if (!data || data.length === 0) return null;
    const total = data.reduce((acc, d) => acc + d.total_polls, 0);
    if (total === 0) return null;
    const down = data.reduce((acc, d) => acc + d.down_polls, 0);
    return ((total - down) / total) * 100;
  }, [data]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
      <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
        <svg
          viewBox={`0 0 ${width} ${squareH}`}
          preserveAspectRatio="none"
          width="100%"
          height={squareH}
          role="img"
          aria-label={`90-day uptime for ${service}`}
        >
          {squares.map((sq, idx) => {
            const x = idx * (squareW + gap);
            return (
              <Tooltip key={sq.date} title={tooltipText(sq)} arrow placement="top">
                <rect
                  x={x}
                  y={0}
                  width={squareW}
                  height={squareH}
                  rx={2}
                  ry={2}
                  fill={colourFor(theme, sq.data)}
                  style={{ cursor: 'default' }}
                />
              </Tooltip>
            );
          })}
        </svg>
      </Box>
      {staff && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums', minWidth: 76, textAlign: 'right' }}
        >
          {overallPct === null ? '— · ' : `${overallPct.toFixed(2)}% · `}{days}d
        </Typography>
      )}
    </Box>
  );
}
